import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Table,
  PageHeader,
  Button,
  Dropdown,
  Space,
  Tooltip,
  Menu,
  Modal,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { samplerListingSaga } from "./saga";
import * as selectors from "./selectors";
import { MoreOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import addNewSamplerSchema from "../../../../schemas/addNewSamplerSchema";
import { useForm } from "react-hook-form";
import AddNewSamplerForm from "../../../../forms/AddNewSamplerForm";
import { notification } from "antd";
import routes from "../../../../../routes";
import { useAuth } from "../../../../contexts/auth";
import { reverse } from "named-urls";

const STORAGE_KEY = 'samplerListingFilters';

const SamplerListing = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: samplerListingSaga });
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;

  const [addNewSamplerModal, setAddNewSamplerModal] = useState(false);
  const [isSamplerAdding, setIsSamplerAdding] = useState(false);
  const [isLoadingOperatingDistricts, setIsLoadingOperatingDistricts] = useState(false);

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }

    return {
      page: 1,
      pageSize: 10,
      filters: { state: null },
    };
  };

  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [filters, setFilters] = useState(initialState.filters);

  const samplers = useSelector(selectors.selectSamplers) || [];
  const tableLoading = useSelector(selectors.selectTableLoading);
  const samplerCount = useSelector(selectors.selectSamplerCount);
  const districtsOfMultipleStates = useSelector(
    selectors.selectDistrictsOfMultipleStates
  );
  const districts = useSelector(selectors.selectDistricts) || [];
  const states = useSelector(selectors.selectStatesSorted) || [];
  const agencies = useSelector(selectors.selectAgencies) || [];

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = {
      page: currentPage,
      pageSize,
      filters,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, pageSize, filters]);

  // Fetch samplers when filters change
  useEffect(() => {
    dispatch(
      actions.fetchSamplers({
        page: currentPage,
        limit: pageSize,
        filters,
      })
    );
  }, [dispatch, currentPage, pageSize, filters]);

  // Fetch states on mount
  useEffect(() => {
    dispatch(actions.fetchStates());
  }, [dispatch]);

  const samplerData = samplers
    ? samplers
      .filter((sampler) => {
        if (userRole === "csc_admin") {
          return sampler.agency?.agencyName === "CSC e-Governance Services India Ltd.";
        }
        return true;
      })
      .map((sampler) => ({
        _id: sampler._id,
        key: sampler._id,
        name: sampler.name,
        samplerContact: sampler.samplerContact,
        address: sampler.address,
        pincode: sampler.pincode,
        email: sampler.email,
        district: sampler.district.name,
        state: sampler.state.name,
        operatingStates: sampler.operatingStates,
        operatingDistricts: sampler.operatingDistricts,
        agency: sampler.agency.agencyName,
      }))
    : [];

  const getStateFilters = () => {
    return states.map((state) => ({
      text: state.name,
      value: state._id,
    }));
  };

  const getMenuItems = (record) => {
    return (
      <Menu>
        <Menu.Item
          key="edit"
          onClick={() => {
            history.push(
              reverse(`${routes.backOffice.samplers.details.edit}`, {
                samplerId: record._id,
              })
            );
          }}
          style={{ minWidth: "100px" }}
        >
          Edit
        </Menu.Item>
      </Menu>
    );
  };

  const handleTablePagination = (pagination, newFilters, sorter) => {
    const isPageSizeChanged = pagination.pageSize !== pageSize;
    const isFilterChanged = JSON.stringify(newFilters) !== JSON.stringify(filters);
    const newPage = isPageSizeChanged || isFilterChanged ? 1 : pagination.current;

    setCurrentPage(newPage);
    setPageSize(pagination.pageSize);
    setFilters(newFilters?.state ? { state: newFilters.state } : { state: null });
  };

  const {
    control: addNewSamplerControl,
    handleSubmit: addNewSamplerHandleSubmit,
    formState: { errors: addNewSamplerErrors },
    reset: resetAddNewSamplerForm,
    watch: addNewSamplerWatch,
    setValue: addNewSamplerSetValue,
  } = useForm({
    resolver: yupResolver(addNewSamplerSchema),
    defaultValues: {
      name: "",
      samplerContact: "",
      address: "",
      pincode: "",
      email: "",
      district: "",
      state: "",
      operatingStates: [],
      operatingDistricts: [],
      agency: "",
    },
  });

  useEffect(() => {
    if (addNewSamplerModal) {
      dispatch(actions.fetchDistricts());
      dispatch(actions.fetchAgencies());
    }
  }, [dispatch, addNewSamplerModal]);

  const selectedState = addNewSamplerWatch("state");
  const selectedOperatingStates = addNewSamplerWatch("operatingStates");
  const currentOperatingDistricts = addNewSamplerWatch("operatingDistricts");

  useEffect(() => {
    if (selectedState) {
      dispatch(actions.fetchDistricts({ stateId: selectedState }));
      addNewSamplerSetValue("district", "");
    }
  }, [dispatch, selectedState, addNewSamplerSetValue]);

  useEffect(() => {
    if (selectedOperatingStates && selectedOperatingStates.length > 0) {
      setIsLoadingOperatingDistricts(true);
      dispatch(
        actions.fetchDistrictsOfMultipleStates({
          stateIds: selectedOperatingStates,
          onSuccess: () => {
            setIsLoadingOperatingDistricts(false);
          },
          onFailure: () => {
            setIsLoadingOperatingDistricts(false);
          },
        })
      );
    } else {
      addNewSamplerSetValue("operatingDistricts", []);
    }
  }, [dispatch, selectedOperatingStates, addNewSamplerSetValue]);

  useEffect(() => {
    if (districtsOfMultipleStates && districtsOfMultipleStates.length > 0) {
      const validDistrictIds = districtsOfMultipleStates.map(
        (district) => district._id
      );
      const currentValue = currentOperatingDistricts || [];
      const filteredOperatingDistricts = currentValue.filter((districtId) =>
        validDistrictIds.includes(districtId)
      );

      addNewSamplerSetValue("operatingDistricts", filteredOperatingDistricts);
    } else {
      addNewSamplerSetValue("operatingDistricts", []);
    }
  }, [dispatch, districtsOfMultipleStates, addNewSamplerSetValue]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "Sampler Contact",
      dataIndex: "samplerContact",
      key: "samplerContact",
      width: 200,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 200,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      filters: getStateFilters(),
      filteredValue: filters?.state || null,
      filterMode: "menu",
      filterSearch: true,
      width: 150,
    },
    {
      title: "District",
      dataIndex: "district",
      key: "district",
      width: 150,
    },
    {
      title: "Operating States",
      dataIndex: "operatingStates",
      key: "operatingStates",
      width: 250,
      render: (operatingStates) => {
        if (Array.isArray(operatingStates) && operatingStates.length > 0) {
          return operatingStates.map((state) => state.name).join(", ");
        }
        return "-";
      },
    },
    {
      title: "Operating Districts",
      dataIndex: "operatingDistricts",
      key: "operatingDistricts",
      width: 250,
      render: (operatingDistricts) => {
        if (
          Array.isArray(operatingDistricts) &&
          operatingDistricts.length > 0
        ) {
          return operatingDistricts.map((district) => district.name).join(", ");
        }
        return "-";
      },
    },
    {
      title: "Pincode",
      dataIndex: "pincode",
      key: "pincode",
      width: 150,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Agency",
      dataIndex: "agency",
      key: "agency",
      width: 250,
    },
  ];

  if (userRole !== "agnext_reviewer" && userRole !== "support_team" && userRole !== "csc_admin") {
    columns.push({
      width: 50,
      render: (text, record) => (
        <Space size="middle">
          <Dropdown overlay={getMenuItems(record)}>
            <Tooltip title="Actions">
              <Button type="text" shape="circle" icon={<MoreOutlined />} />
            </Tooltip>
          </Dropdown>
        </Space>
      ),
    });
  }

  const onAddNewSamplerSubmit = (data) => {
    setIsSamplerAdding(true);
    dispatch(
      actions.addSampler({
        data,
        currentPage,
        currentLimit: pageSize,
        currentFilters: filters,
        onSuccess: (response) => {
          notification.success({
            message: "Sampler added successfully!",
          });
          setAddNewSamplerModal(false);
          setIsSamplerAdding(false);
          resetAddNewSamplerForm();
        },
        onFailure: (error) => {
          setIsSamplerAdding(false);
          notification.error({
            message: error?.message || "Failure in adding sampler",
          });
        },
      })
    );
  };

  return (
    <>
      <div style={{ height: "100%" }}>
        <PageHeader
          ghost={false}
          title="Sampler Listing"
          extra={
            userRole !== "agnext_reviewer" && userRole !== "support_team" && userRole !== "csc_admin"
              ? [
                <Button
                  key="create"
                  type="primary"
                  onClick={() => setAddNewSamplerModal(!addNewSamplerModal)}
                >
                  Add New Sampler
                </Button>,
              ]
              : []
          }
        />
        <div
          style={{
            height: "calc(100vh - 137px)",
            overflowY: "auto",
            padding: 24,
          }}
        >
          <Table
            dataSource={samplerData}
            columns={columns}
            loading={tableLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              pageSizeOptions: ["10", "30", "50", "100"],
              showQuickJumper: true,
              showSizeChanger: true,
              total: samplerCount,
              disabled: samplerData.length === 0,
            }}
            onChange={handleTablePagination}
            scroll={{ x: 1450 }}
          />
        </div>
      </div>
      <Modal
        title="Add New Sampler"
        visible={addNewSamplerModal}
        onCancel={() => {
          setAddNewSamplerModal(false);
          resetAddNewSamplerForm();
        }}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setAddNewSamplerModal(false);
              resetAddNewSamplerForm();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isSamplerAdding}
            disabled={isLoadingOperatingDistricts}
            onClick={addNewSamplerHandleSubmit(onAddNewSamplerSubmit)}
          >
            Submit
          </Button>,
        ]}
      >
        <AddNewSamplerForm
          control={addNewSamplerControl}
          errors={addNewSamplerErrors}
          watch={addNewSamplerWatch}
          isLoadingOperatingDistricts={isLoadingOperatingDistricts}
          options={{
            states: states.map((state) => ({
              label: state.name,
              value: state._id,
            })),
            districts: districts.map((district) => ({
              label: district.city,
              value: district._id,
            })),
            agencies: agencies.map((agency) => ({
              label: agency.agencyName,
              value: agency._id,
            })),
            districtsOfMultipleStates: districtsOfMultipleStates.map(
              (district) => ({
                label: district.districtName,
                value: district._id,
              })
            ),
          }}
        />
      </Modal>
    </>
  );
};

export default SamplerListing;